// Getting someone's attention when the page is not what they are looking at.
//
// The incoming-call card is the mechanism, but a card is only seen by someone
// looking at the tab. Two things reach past that, and neither can be assumed to
// work — so both are attempts, and the caller is told which one landed:
//
//   the tab title   always available, works in a background tab, costs nothing
//   a notification  needs permission, which is asked for at the first call and
//                   never at load: a permission prompt on a page someone just
//                   opened is refused on reflex, and refused is permanent.
//
// The pure parts are separated out so they can be tested without a browser.

const FLASH_MS = 1100;

let flashTimer = null;
let flashBase = "";
let flashFrames = [];

/** The two titles a flashing tab alternates between. */
export function titleFrames(base, alert) {
  return [`🔴 ${alert}`, base || alert];
}

/**
 * Whether a desktop notification is the right call. Not when the page is
 * already in front of the person: a notification for something they can see is
 * noise, and it trains them to turn notifications off.
 */
export function shouldNotify({ supported, permission, visibility }) {
  if (!supported || permission !== "granted") return false;
  return visibility === "hidden";
}

// Asked once, ever. A person who said no to being notified about calls said no;
// asking again on the next missed one is how an app earns a permanent "block".
const ASKED_KEY = "vs_call_notify_asked";

export function notifyAskedBefore() {
  try { return localStorage.getItem(ASKED_KEY) === "1"; } catch { return false; }
}

export function rememberNotifyAsked() {
  try { localStorage.setItem(ASKED_KEY, "1"); } catch { /* private mode: we just ask once more */ }
}

/**
 * Whether to offer notifications, at the moment a call has just been missed.
 *
 * Not at load, and not while the phone is ringing either: a bare permission
 * prompt in both those moments is refused on reflex, and a refusal is permanent.
 * Right after a missed call the question answers itself — the person has just
 * lost something they would have wanted.
 *
 * `permission` must be "default": "granted" needs no asking and "denied" cannot
 * be asked again from a page.
 */
export function shouldOfferNotifications({ supported, permission, askedBefore }) {
  return Boolean(supported) && permission === "default" && !askedBefore;
}

export function notificationsSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function notificationPermission() {
  return notificationsSupported() ? Notification.permission : "unsupported";
}

/**
 * Ask, once, at a moment the request makes sense. "default" means never asked;
 * "denied" is final and is not asked again — a second prompt is not possible and
 * pretending otherwise hides the real state from the caller.
 */
export async function askNotificationPermission() {
  if (!notificationsSupported()) return "unsupported";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export function startTitleAlert(alert) {
  if (typeof document === "undefined") return false;
  stopTitleAlert();
  flashBase = document.title;
  flashFrames = titleFrames(flashBase, alert);
  let i = 0;
  document.title = flashFrames[0];
  flashTimer = setInterval(() => {
    i = (i + 1) % flashFrames.length;
    document.title = flashFrames[i];
  }, FLASH_MS);
  return true;
}

export function stopTitleAlert() {
  if (flashTimer) { clearInterval(flashTimer); flashTimer = null; }
  // Only restore what we changed. Something else may have set the title while
  // the tab was flashing (the branding effect does, on a workspace switch), and
  // putting our stale copy back would undo it.
  if (typeof document !== "undefined" && flashFrames.includes(document.title)) {
    document.title = flashBase;
  }
  flashFrames = [];
  flashBase = "";
}

/**
 * A desktop notification, if one is warranted and allowed. Returns the
 * Notification so the caller can close it when the call is answered — a
 * notification for a call that is over is worse than none.
 */
export function notifyDesktop({ title, body, tag, onClick }) {
  if (!shouldNotify({
    supported: notificationsSupported(),
    permission: notificationPermission(),
    visibility: typeof document === "undefined" ? "visible" : document.visibilityState,
  })) return null;
  try {
    const n = new Notification(title, { body, tag, requireInteraction: true });
    n.onclick = () => {
      try { window.focus(); } catch { /* not permitted in every browser */ }
      n.close();
      onClick?.();
    };
    return n;
  } catch {
    return null;   // Chrome throws for Notification without a service worker on some platforms
  }
}
